import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import axios from "axios";
import {
  ShoppingBag,
  Dumbbell,
  Heart,
  Wallet,
  ChevronRight,
  Menu,
  X,
  ShoppingCart,
  Plus,
  Check,
  Gift,
  Zap,
  Star,
  TrendingUp,
  Award,
  CreditCard,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [addedProducts, setAddedProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    fetchProducts();
    if (user) {
      fetchCartCount();
    }
  }, [user]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCartCount = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await axios.get(`${API_URL}/api/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartCount(response.data.items?.length || 0);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  const handleAddToCart = async (product) => {
    if (!user) {
      // Store product in localStorage and redirect to login
      localStorage.setItem("pending_cart_item", JSON.stringify(product));
      navigate("/login");
      return;
    }

    try {
      const token = localStorage.getItem("session_token");
      await axios.post(
        `${API_URL}/api/cart/add`,
        {
          product_id: product.product_id,
          quantity: 1,
          size: product.sizes?.[0] || null,
          color: product.colors?.[0] || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAddedProducts(prev => ({ ...prev, [product.product_id]: true }));
      setCartCount(prev => prev + 1);
      setTimeout(() => {
        setAddedProducts(prev => ({ ...prev, [product.product_id]: false }));
      }, 2000);
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Group products by category
  const wearProducts = products.filter(p => p.category === "wear");
  const fitProducts = products.filter(p => p.category === "fit");
  const lifeProducts = products.filter(p => p.category === "life");

  const categories = [
    { id: "all", name: "All Products", icon: Star },
    { id: "wear", name: "BE Wear", icon: ShoppingBag, description: "Apparel & Footwear" },
    { id: "fit", name: "BE Fit", icon: Dumbbell, description: "Supplements & Nutrition" },
    { id: "life", name: "BE Life", icon: Heart, description: "Lifestyle Essentials" },
  ];

  const filteredProducts = activeCategory === "all" 
    ? products 
    : products.filter(p => p.category === activeCategory);

  const ProductCard = ({ product, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
      data-testid={`product-card-${product.product_id}`}
    >
      <div className="aspect-square bg-gray-50 relative overflow-hidden">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
            product.category === 'wear' ? 'bg-primary/10 text-primary' :
            product.category === 'fit' ? 'bg-secondary/10 text-secondary' :
            'bg-purple-100 text-purple-600'
          }`}>
            {product.category === 'wear' ? 'BE Wear' : 
             product.category === 'fit' ? 'BE Fit' : 'BE Life'}
          </span>
        </div>
        <button
          onClick={() => handleAddToCart(product)}
          className={`absolute bottom-3 right-3 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
            addedProducts[product.product_id]
              ? "bg-green-500 text-white scale-110"
              : "bg-primary text-white hover:bg-primary/90 hover:scale-110"
          }`}
          data-testid={`add-to-cart-${product.product_id}`}
        >
          {addedProducts[product.product_id] ? (
            <Check size={18} />
          ) : (
            <Plus size={18} />
          )}
        </button>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">{product.name}</h3>
        <p className="text-sm text-gray-500 mb-3 line-clamp-2 h-10">{product.description}</p>
        <div className="flex items-center justify-between">
          <span className="font-bold text-lg text-gray-900">{formatPrice(product.price)}</span>
          {product.sizes?.length > 0 && (
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
              {product.sizes.length} sizes
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );

  const walletBenefits = [
    { icon: Gift, title: "500 Welcome Points", description: "Get KES 500 worth of points when you sign up" },
    { icon: TrendingUp, title: "Earn on Every Purchase", description: "Get 5 points for every KES 100 spent" },
    { icon: Award, title: "Exclusive Rewards", description: "Redeem points for discounts and free products" },
    { icon: Zap, title: "Refer & Earn", description: "Get 300 points for every friend who joins" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_fitechglobal/artifacts/7g0ygqsm_be%20logo.jpg" 
                alt="BE Lifestyle" 
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Category Links */}
            <div className="hidden md:flex items-center gap-6">
              {categories.slice(1).map(cat => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`text-sm font-medium transition-colors ${
                    activeCategory === cat.id ? 'text-primary' : 'text-gray-600 hover:text-primary'
                  }`}
                  data-testid={`nav-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
              <button
                onClick={() => document.getElementById('wallet')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm font-medium text-gray-600 hover:text-primary transition-colors"
              >
                BE Wallet
              </button>
            </div>

            <div className="flex items-center gap-3">
              <Link 
                to="/cart" 
                className="relative p-2 text-gray-600 hover:text-primary transition-colors"
                data-testid="cart-btn"
              >
                <ShoppingCart size={22} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {cartCount}
                  </span>
                )}
              </Link>
              
              {user ? (
                <Button 
                  size="sm" 
                  onClick={() => navigate("/dashboard")} 
                  data-testid="dashboard-btn"
                  className="hidden sm:flex"
                >
                  My Account
                </Button>
              ) : (
                <Button 
                  size="sm" 
                  onClick={() => navigate("/login")} 
                  data-testid="login-btn"
                  className="hidden sm:flex"
                >
                  Sign In
                </Button>
              )}

              <button
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
            >
              <div className="px-4 py-4 space-y-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setMobileMenuOpen(false);
                      document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={`block w-full text-left py-2 px-3 rounded-lg transition-colors ${
                      activeCategory === cat.id 
                        ? 'bg-primary/10 text-primary font-medium' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
                <div className="pt-2 border-t border-gray-100">
                  {user ? (
                    <Button onClick={() => navigate("/dashboard")} className="w-full">
                      My Account
                    </Button>
                  ) : (
                    <Button onClick={() => navigate("/login")} className="w-full">
                      Sign In
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Banner */}
      <section className="pt-20 pb-8 bg-gradient-to-br from-primary via-primary to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block bg-white/20 backdrop-blur-sm text-sm font-medium px-3 py-1 rounded-full mb-4">
                🇰🇪 Now in Kenya
              </span>
              <h1 className="font-bold text-3xl md:text-5xl mb-4 leading-tight">
                Own Your Journey
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-6">
                Premium fashion, fitness supplements & lifestyle essentials. All BE branded. Free delivery in Nairobi on orders over KES 5,000.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button 
                  size="lg"
                  onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white text-primary hover:bg-white/90 font-bold px-6"
                  data-testid="shop-now-btn"
                >
                  Shop Now
                  <ChevronRight className="ml-1" size={18} />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('wallet')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-white/30 text-white hover:bg-white/10 font-bold px-6"
                >
                  <Wallet className="mr-2" size={18} />
                  Learn about BE Wallet
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-16 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 py-3 overflow-x-auto hide-scrollbar">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <cat.icon size={16} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section id="products" className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-bold text-2xl text-gray-900">
                {activeCategory === 'all' ? 'All Products' : 
                 activeCategory === 'wear' ? 'BE Wear - Apparel & Footwear' :
                 activeCategory === 'fit' ? 'BE Fit - Supplements & Nutrition' :
                 'BE Life - Lifestyle Essentials'}
              </h2>
              <p className="text-gray-500 mt-1">{filteredProducts.length} products</p>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-full" />
                    <div className="h-5 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div 
              layout
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            >
              <AnimatePresence mode="popLayout">
                {filteredProducts.map((product, index) => (
                  <ProductCard key={product.product_id} product={product} index={index} />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* BE Wallet Section */}
      <section id="wallet" className="py-16 md:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full mb-4">
                <Wallet size={18} />
                <span className="font-medium">BE Wallet</span>
              </div>
              <h2 className="font-bold text-3xl md:text-4xl mb-4">
                Shop Smarter. Earn More.
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                BE Wallet rewards you for every purchase. Earn points, unlock exclusive deals, and get access to members-only drops.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {walletBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-primary/50 transition-colors"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="text-primary" size={24} />
                </div>
                <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-primary to-orange-500 rounded-2xl p-8 md:p-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="font-bold text-2xl mb-2">Start Earning Today</h3>
                <p className="text-white/80">
                  Create your free account and get 500 welcome points instantly. That's KES 500 to spend on your first order!
                </p>
              </div>
              <Button
                size="lg"
                onClick={() => navigate("/login")}
                className="bg-white text-primary hover:bg-white/90 font-bold px-8 whitespace-nowrap"
                data-testid="wallet-cta-btn"
              >
                <CreditCard className="mr-2" size={18} />
                Get BE Wallet
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Delivery Info */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: "🚚", title: "Free Delivery", desc: "Orders over KES 5,000 in Nairobi" },
              { icon: "🔒", title: "Secure Payment", desc: "M-Pesa, Cards & BE Wallet" },
              { icon: "↩️", title: "Easy Returns", desc: "7-day return policy" },
              { icon: "💬", title: "24/7 Support", desc: "Chat with us anytime" },
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center">
                <span className="text-3xl mb-2">{item.icon}</span>
                <h4 className="font-semibold text-gray-900">{item.title}</h4>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="bg-white rounded-lg p-2 inline-block mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fitechglobal/artifacts/7g0ygqsm_be%20logo.jpg" 
                  alt="BE Lifestyle" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Africa's premier lifestyle brand. Fashion. Fitness. Fintech.
              </p>
              {/* Social Media Links */}
              <div className="flex items-center gap-3">
                <a 
                  href="https://instagram.com/belifestyle" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://tiktok.com/@belifestyle" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                  aria-label="TikTok"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                <a 
                  href="https://x.com/belifestyle" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                  aria-label="X (Twitter)"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a 
                  href="https://facebook.com/belifestyle" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://linkedin.com/company/belifestyle" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Shop</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => setActiveCategory('wear')} className="hover:text-white">BE Wear</button></li>
                <li><button onClick={() => setActiveCategory('fit')} className="hover:text-white">BE Fit</button></li>
                <li><button onClick={() => setActiveCategory('life')} className="hover:text-white">BE Life</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white">Delivery Info</a></li>
                <li><a href="#" className="hover:text-white">Returns</a></li>
                <li><a href="#" className="hover:text-white">Size Guide</a></li>
                <li><a href="#" className="hover:text-white">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>📍 Nairobi, Kenya</li>
                <li>📞 +254 700 000 000</li>
                <li>✉️ hello@belifestyle.co.ke</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © 2025 BE Lifestyle Holdings. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-500">
              <a href="#" className="hover:text-white">Privacy</a>
              <a href="#" className="hover:text-white">Terms</a>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS for hiding scrollbar */}
      <style>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
